/**
 * MBA Lab — pure state model for the Leadership Practice Journal: one
 * local-first store covering three tabs (90-day practice, weekly reflection,
 * decision journal), matching the plan's "one tool, three tabs, one store,
 * one privacy/export/delete story." No DOM, storage, or analytics
 * dependencies - leadership-practice-journal.js owns all of that.
 *
 * Timestamps are never generated inside this module (no Date.now()) so every
 * function here stays a pure, synchronously-testable transform - callers pass
 * the current time in, matching charter-model.js's autosave() precedent.
 *
 * This surfaces personal patterns without diagnosing the user: entries are
 * simply stored and made browsable in full, so the person reviews their own
 * history and draws their own conclusions - there is deliberately no
 * auto-generated label, score, or diagnosis about the person's patterns here.
 */

import { DAILY_PROMPTS, PHASES, SUGGESTED_LOG_TYPE_BY_DAY } from './journal-prompts.js';

export const SCHEMA_NAME = 'mbalab.leadership-practice-journal';
export const SCHEMA_VERSION = 2;
export const TOTAL_DAYS = 90;

export const LIMITS = Object.freeze({
  dailyReflection: 1000,
  weeklyField: 800,
  decisionShortField: 500,
  decisionLongField: 1000,
  emotionalState: 300,
  maxDecisionEntries: 300,
  maxWeeklyEntries: 100,
  // Sized for the worst case the caps above actually permit (300 decisions x
  // ~5.3KB/entry at max field length + 100 weekly x ~2.4KB + 90 daily x 1KB +
  // JSON structural overhead, comfortably rounded up) - the original 200KB
  // was smaller than what this file's own entry caps allow, so a genuinely
  // heavy (not malicious) user could silently stop being able to save partway
  // through normal use.
  maxSerializedBytes: 3 * 1024 * 1024,
});

const DECISION_STATUS = Object.freeze({ OPEN: 'open', RESOLVED: 'resolved' });

export function createInitialState() {
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: null,
    dailyPractice: { entries: {} }, // { [day: string]: { reflection, completedAt } }
    closingPlan: null, // { mostWorthContinuing, nextFocus, completedAt } | null
    weeklyReflections: [], // [{ id, pattern, wouldChange, nextFocus, createdAt }]
    decisionJournal: [], // [{ id, status, decision, factsKnown, assumptions, emotionalState, stakeholders, expectedResult, actualResult, repeatOrChange, createdAt, resolvedAt }]
  };
}

function makeEntryId(prefix) {
  return `${prefix}_` + Math.random().toString(36).slice(2, 10);
}

function clampText(value, maxLen) {
  return typeof value === 'string' ? value.trim().slice(0, maxLen) : '';
}

export function getPromptForDay(day) {
  const index = (Math.max(1, day) - 1) % DAILY_PROMPTS.length;
  return DAILY_PROMPTS[index];
}

/** The phase a given day falls in, or null for a day outside 1-90 (shouldn't
 * happen given TOTAL_DAYS, but PHASES' own day ranges are the source of
 * truth rather than assuming every day matches one). */
export function getPhaseForDay(day) {
  return PHASES.find((p) => day >= p.days[0] && day <= p.days[1]) ?? null;
}

/** The suggested log-type label for a day, or null - most days have none.
 * Purely a UI hint (which label/framing shows above the reflection field);
 * the stored entry shape is identical either way. */
export function getSuggestedLogTypeForDay(day) {
  return SUGGESTED_LOG_TYPE_BY_DAY[day] ?? null;
}

/**
 * The next genuinely unlogged day, capped at TOTAL_DAYS + 1 (meaning "all 90
 * logged, show the closing plan").
 *
 * Scans for the first missing day rather than counting entries: a count only
 * equals "the next unlogged day" while the logged days happen to form an
 * unbroken 1..k prefix. Any gap - a partially-restored or hand-edited save, or
 * a future edit/delete-entry feature - would otherwise make this point at a day
 * that is *already* logged, and since logDailyEntry writes by day number, every
 * subsequent submit would overwrite that same day while the gap day was never
 * offered and isDailyPracticeComplete could never become true.
 */
export function getCurrentDay(state) {
  for (let day = 1; day <= TOTAL_DAYS; day++) {
    if (!state.dailyPractice.entries[day]) return day;
  }
  return TOTAL_DAYS + 1;
}

export function isDailyPracticeComplete(state) {
  return getCurrentDay(state) > TOTAL_DAYS;
}

export function logDailyEntry(state, day, reflection, completedAt) {
  if (!Number.isInteger(day) || day < 1 || day > TOTAL_DAYS) return state;
  const clamped = clampText(reflection, LIMITS.dailyReflection);
  if (!clamped) return state;
  return {
    ...state,
    dailyPractice: {
      entries: { ...state.dailyPractice.entries, [String(day)]: { reflection: clamped, completedAt } },
    },
  };
}

export function setClosingPlan(state, { mostWorthContinuing, nextFocus }, completedAt) {
  if (!isDailyPracticeComplete(state)) return state; // only available after all TOTAL_DAYS days
  return {
    ...state,
    closingPlan: {
      mostWorthContinuing: clampText(mostWorthContinuing, LIMITS.weeklyField),
      nextFocus: clampText(nextFocus, LIMITS.weeklyField),
      completedAt,
    },
  };
}

export function addWeeklyReflection(state, { pattern, wouldChange, nextFocus }, createdAt) {
  if (state.weeklyReflections.length >= LIMITS.maxWeeklyEntries) return state;
  const entry = {
    id: makeEntryId('week'),
    pattern: clampText(pattern, LIMITS.weeklyField),
    wouldChange: clampText(wouldChange, LIMITS.weeklyField),
    nextFocus: clampText(nextFocus, LIMITS.weeklyField),
    createdAt,
  };
  if (!entry.pattern && !entry.wouldChange && !entry.nextFocus) return state;
  return { ...state, weeklyReflections: [...state.weeklyReflections, entry] };
}

export function addDecisionEntry(state, fields, createdAt) {
  if (state.decisionJournal.length >= LIMITS.maxDecisionEntries) return state;
  const decision = clampText(fields.decision, LIMITS.decisionShortField);
  if (!decision) return state;

  const entry = {
    id: makeEntryId('dec'),
    status: DECISION_STATUS.OPEN,
    decision,
    factsKnown: clampText(fields.factsKnown, LIMITS.decisionLongField),
    assumptions: clampText(fields.assumptions, LIMITS.decisionLongField),
    emotionalState: clampText(fields.emotionalState, LIMITS.emotionalState),
    stakeholders: clampText(fields.stakeholders, LIMITS.decisionShortField),
    expectedResult: clampText(fields.expectedResult, LIMITS.decisionLongField),
    actualResult: '',
    repeatOrChange: '',
    createdAt,
    resolvedAt: null,
  };
  return { ...state, decisionJournal: [...state.decisionJournal, entry] };
}

/** Records the outcome of a previously-logged decision - always happens later
 * than addDecisionEntry(), once the actual result is known. */
export function recordDecisionOutcome(state, entryId, { actualResult, repeatOrChange }, resolvedAt) {
  const exists = state.decisionJournal.some((e) => e.id === entryId);
  if (!exists) return state;
  return {
    ...state,
    decisionJournal: state.decisionJournal.map((e) =>
      e.id === entryId
        ? {
            ...e,
            status: DECISION_STATUS.RESOLVED,
            actualResult: clampText(actualResult, LIMITS.decisionLongField),
            repeatOrChange: clampText(repeatOrChange, LIMITS.decisionLongField),
            resolvedAt,
          }
        : e
    ),
  };
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};

  const rawDailyEntries = r.dailyPractice && typeof r.dailyPractice === 'object' ? r.dailyPractice.entries : null;
  const entries = {};
  if (rawDailyEntries && typeof rawDailyEntries === 'object') {
    for (const [dayKey, value] of Object.entries(rawDailyEntries)) {
      const day = Number(dayKey);
      if (!Number.isInteger(day) || day < 1 || day > TOTAL_DAYS) continue;
      if (!value || typeof value !== 'object') continue;
      const reflection = clampText(value.reflection, LIMITS.dailyReflection);
      if (!reflection) continue;
      entries[String(day)] = { reflection, completedAt: typeof value.completedAt === 'number' ? value.completedAt : null };
    }
  }

  let closingPlan = null;
  if (r.closingPlan && typeof r.closingPlan === 'object') {
    const mostWorthContinuing = clampText(r.closingPlan.mostWorthContinuing, LIMITS.weeklyField);
    const nextFocus = clampText(r.closingPlan.nextFocus, LIMITS.weeklyField);
    if (mostWorthContinuing || nextFocus) {
      closingPlan = {
        mostWorthContinuing,
        nextFocus,
        completedAt: typeof r.closingPlan.completedAt === 'number' ? r.closingPlan.completedAt : null,
      };
    }
  }

  const weeklyReflections = Array.isArray(r.weeklyReflections)
    ? r.weeklyReflections
        .slice(0, LIMITS.maxWeeklyEntries)
        .filter((e) => e && typeof e === 'object' && typeof e.id === 'string')
        .map((e) => ({
          id: e.id,
          pattern: clampText(e.pattern, LIMITS.weeklyField),
          wouldChange: clampText(e.wouldChange, LIMITS.weeklyField),
          nextFocus: clampText(e.nextFocus, LIMITS.weeklyField),
          createdAt: typeof e.createdAt === 'number' ? e.createdAt : null,
        }))
    : [];

  const decisionJournal = Array.isArray(r.decisionJournal)
    ? r.decisionJournal
        .slice(0, LIMITS.maxDecisionEntries)
        .filter((e) => e && typeof e === 'object' && typeof e.id === 'string' && e.decision)
        .map((e) => ({
          id: e.id,
          status: e.status === DECISION_STATUS.RESOLVED ? DECISION_STATUS.RESOLVED : DECISION_STATUS.OPEN,
          decision: clampText(e.decision, LIMITS.decisionShortField),
          factsKnown: clampText(e.factsKnown, LIMITS.decisionLongField),
          assumptions: clampText(e.assumptions, LIMITS.decisionLongField),
          emotionalState: clampText(e.emotionalState, LIMITS.emotionalState),
          stakeholders: clampText(e.stakeholders, LIMITS.decisionShortField),
          expectedResult: clampText(e.expectedResult, LIMITS.decisionLongField),
          actualResult: clampText(e.actualResult, LIMITS.decisionLongField),
          repeatOrChange: clampText(e.repeatOrChange, LIMITS.decisionLongField),
          createdAt: typeof e.createdAt === 'number' ? e.createdAt : null,
          resolvedAt: typeof e.resolvedAt === 'number' ? e.resolvedAt : null,
        }))
    : [];

  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    dailyPractice: { entries },
    closingPlan,
    weeklyReflections,
    decisionJournal,
  };
}

const MIGRATIONS = {
  0: (state) => ({ ...state, version: 1 }),
  // v1 -> v2: TOTAL_DAYS grew from 30 to 90 (see journal-prompts.js). A v1
  // user's days 1-30 entries stay exactly as they were; they simply become
  // day 31+ eligible instead of "complete" the next time they open the tool.
  //
  // closingPlan is the one field that DOES need to change, not just carry
  // over: it's a reflection written at day-30 completion, for a practice
  // that was 30 days long at the time. Left in place, it would silently
  // resurface as-is once the user re-reaches "complete" at the real day 90
  // (isDailyPracticeComplete flips true again) - stale content presented as
  // their answer for a 90-day arc they hadn't actually finished when they
  // wrote it, with no in-app way to write a fresh one short of deleting all
  // 90 real daily entries to get there. Clearing it here means a genuine
  // day-90 completion correctly prompts a new reflection instead.
  1: (state) => ({ ...state, version: 2, closingPlan: null }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    // Number.isInteger(NaN) is false, so this also rejects a corrupted NaN
    // version - without this, `NaN > SCHEMA_VERSION` and `MIGRATIONS[NaN]`
    // are both silently false/undefined, so a corrupted version would fall
    // through as if it were already current instead of being flagged.
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

/** Escapes markdown-structural characters - identical approach to
 * charter-model.js's/planner-model.js's escapeMarkdown, see either's doc
 * comment for why. A journal entry is exactly the kind of untrusted text
 * this exists for: without it, a reflection containing a line-leading `#`
 * forges a real heading (including a fake second "# Leadership Practice
 * Journal" or "## Closing development plan"), and raw HTML/script tags
 * pass through unescaped into the downloaded file. */
export function escapeMarkdown(text) {
  const escaped = String(text)
    .replace(/\\/g, '\\\\')
    .replace(/([`*_{}[\]()#+!|<>~&])/g, '\\$1');
  return escaped
    // 0-3 leading spaces, not column 0 only - CommonMark allows an indented
    // block marker, so an anchored-at-0 rule let pasted indented bullets and
    // `---`/`===` through as live structure. See charter-model.js's copy.
    .replace(/^( {0,3})([-=>])/gm, '$1\\$2')
    .replace(/^( {0,3})(\d{1,9})\./gm, '$1$2\\.');
}

/** One "**Label:** value" line, escaped - or nothing at all when the value is
 * blank, matching leadership-practice-journal.js's own on-screen
 * appendLabeledText() exactly (which skips rendering the label entirely
 * rather than showing an empty "**Label:** " line). Without this the
 * export and the live preview show genuinely different content for the
 * same state, the drift charter-model.js's single-document-model design
 * exists specifically to prevent. */
function mdField(label, value) {
  return value ? [`**${label}:** ${escapeMarkdown(value)}`] : [];
}

/** Human-readable export (never just raw JSON) covering every entry across
 * all three tabs, for the plan's "export" leg of "local-first, export,
 * delete." Markdown, matching the site's other tool-export convention. */
export function buildMarkdownExport(state) {
  const lines = ['# Leadership Practice Journal', ''];

  const days = Object.keys(state.dailyPractice.entries)
    .map(Number)
    .sort((a, b) => a - b);
  if (days.length) {
    lines.push('## 90-day practice', '');
    for (const day of days) {
      const entry = state.dailyPractice.entries[String(day)];
      lines.push(`### Day ${day}: ${escapeMarkdown(getPromptForDay(day))}`, '', escapeMarkdown(entry.reflection), '');
    }
  }

  // Gated on isDailyPracticeComplete, not just state.closingPlan being
  // truthy, matching exactly how the live UI (renderDailyTab) decides
  // whether to show the saved closing plan - a hand-edited/corrupted save
  // with closingPlan set but fewer than 90 real days logged must not show a
  // closing section the on-screen view would never show.
  if (isDailyPracticeComplete(state) && state.closingPlan) {
    lines.push('## Closing development plan', '');
    lines.push(...mdField('Most worth continuing deliberately', state.closingPlan.mostWorthContinuing));
    lines.push(...mdField('Next focus', state.closingPlan.nextFocus));
    lines.push('');
  }

  if (state.weeklyReflections.length) {
    lines.push('## Weekly reflections', '');
    state.weeklyReflections.forEach((entry, i) => {
      lines.push(`### Week ${i + 1}`, '');
      lines.push(...mdField('Pattern noticed', entry.pattern));
      lines.push(...mdField('Would change', entry.wouldChange));
      lines.push(...mdField('Next focus', entry.nextFocus), '');
    });
  }

  if (state.decisionJournal.length) {
    lines.push('## Decision journal', '');
    state.decisionJournal.forEach((entry, i) => {
      lines.push(`### Decision ${i + 1}: ${escapeMarkdown(entry.decision)}`, '');
      lines.push(...mdField('Facts known', entry.factsKnown));
      lines.push(...mdField('Assumptions', entry.assumptions));
      lines.push(...mdField('Emotional state', entry.emotionalState));
      lines.push(...mdField('Stakeholders', entry.stakeholders));
      lines.push(...mdField('Expected result', entry.expectedResult));
      if (entry.status === DECISION_STATUS.RESOLVED) {
        lines.push(...mdField('Actual result', entry.actualResult));
        lines.push(...mdField('Repeat or change', entry.repeatOrChange));
      } else {
        lines.push('**Outcome:** not yet recorded');
      }
      lines.push('');
    });
  }

  return lines.join('\n');
}
